import { CheckCircle, Error, Info, Warning } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  DialogActions,
  Shadows,
  accordionClasses,
  accordionSummaryClasses,
  alertClasses,
  alpha,
  buttonBaseClasses,
  buttonClasses,
  chipClasses,
  createTheme,
  darken,
  formLabelClasses,
  getContrastRatio,
  iconButtonClasses,
  inputLabelClasses,
  lighten,
  outlinedInputClasses,
  typographyClasses,
} from '@mui/material';
import {
  PickersActionBarProps,
  PickersTextField,
  PickersTextFieldProps,
  pickerDayClasses,
  pickersOutlinedInputClasses,
  usePickerContext,
  yearCalendarClasses,
} from '@mui/x-date-pickers';
import { Ref } from 'react';

import { Theme } from './types';

const SPACING_FACTOR = 8;

const customShadows = Array(25).fill('none') as Shadows;
customShadows[1] = '0 2px 4px 0 rgba(0, 0, 0, 0.05)'; // light
customShadows[2] = '0 2px 4px 0 rgba(0, 0, 0, 0.1)'; // default
customShadows[3] = '0 2px 6px 0 rgba(0, 0, 0, 0.12)'; // hover
customShadows[4] = '0 2px 4px 0 rgba(0, 0, 0, 0.25)'; // dark

const CustomDatePickerTextField = ({
  slotProps,
  sx,
  variant: _variant,
  ref,
  ...restProps
}: PickersTextFieldProps & { ref?: Ref<HTMLDivElement> }) => (
  <PickersTextField
    ref={ref}
    variant="outlined"
    slotProps={{
      ...slotProps,
      inputLabel: {
        ...slotProps?.inputLabel,
        shrink: true,
      },
      input: {
        ...slotProps?.input,
        notched: false,
      },
      // `required` would otherwise make the browser validate the picker's hidden
      // input, which can't be focused to show the message. Forms validate
      // through react-hook-form, so `required` only marks the label.
      htmlInput: {
        ...slotProps?.htmlInput,
        required: false,
      },
    }}
    sx={{
      ...sx,
      // There's seemingly a bug in MUI where the MUI-X PickerOutlinedInput's styles
      // are not correctly applied when notched is false
      [`& .${pickersOutlinedInputClasses.notchedOutline} > legend`]: {
        maxWidth: '0.01px',
      },
    }}
    {...restProps}
  />
);

const CustomDatePickerActionBar = ({
  actions,
  sx,
  ...restProps
}: PickersActionBarProps) => {
  const { acceptValueChanges, cancelValueChanges } = usePickerContext();

  const actionButtons = actions?.map((action) => {
    switch (action) {
      case 'cancel':
        return (
          <Button onClick={cancelValueChanges} variant="minimal">
            Cancel
          </Button>
        );
      case 'accept':
        return <Button onClick={acceptValueChanges}>Select Date</Button>;
      default:
        return null;
    }
  });

  return actionButtons?.length ? (
    <DialogActions
      sx={{
        ...sx,
        px: 2,
        pb: 2,
        justifyContent: 'space-between',
      }}
      {...restProps}
    >
      {actionButtons}
    </DialogActions>
  ) : null;
};
export const createMuiTheme = (orgTheme: Theme) => {
  const alertTextColor = (color: string, amount: number) => {
    const isDarkBackground =
      getContrastRatio(orgTheme.contentBackground, '#FFFFFF') > 3;
    return isDarkBackground ? lighten(color, amount) : darken(color, amount);
  };

  return createTheme({
    modularCssLayers: '@layer global, mui;',
    spacing: SPACING_FACTOR,
    typography: {
      fontFamily: orgTheme.fontFamily,
      fontSize: orgTheme.baseFontSize,
      fontWeightRegular: 400,
      fontWeightBold: 600,
      h1: {
        fontSize: '1.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 600,
      },
      h2: {
        fontSize: '1.25rem',
        margin: '0 0 1rem 0',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1rem',
        fontWeight: 600,
        margin: '0 0 1rem 0',
      },
    },
    palette: {
      background: {
        default: orgTheme.appBackground,
        paper: orgTheme.contentBackground,
      },
      primary: {
        main: orgTheme.primary,
        light: lighten(orgTheme.primary, 0.1),
        dark: darken(orgTheme.primary, 0.1),
        contrastText: orgTheme.primaryContrast,
      },
      error: {
        main: orgTheme.negative,
        light: lighten(orgTheme.negative, 0.1),
        dark: darken(orgTheme.negative, 0.1),
        contrastText: orgTheme.contentContrast,
      },
      success: {
        main: orgTheme.positive,
        light: lighten(orgTheme.positive, 0.1),
        dark: darken(orgTheme.positive, 0.1),
        contrastText: orgTheme.contentContrast,
      },
      text: {
        primary: orgTheme.contentPrimary,
        secondary: orgTheme.contentSecondary,
        disabled: orgTheme.contentTertiary,
      },
      divider: orgTheme.faintBorder,
      border: {
        faint: orgTheme.faintBorder,
        medium: orgTheme.mediumBorder,
        element: orgTheme.elementBorder,
      },
    },
    shadows: customShadows,
    components: {
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            margin: 0,
            '&:before': {
              display: 'none',
            },
            [`&.${accordionClasses.expanded}`]: {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          expandIcon: <ExpandMoreIcon sx={{ width: 25, height: 25 }} />,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            fontWeight: 500,
            fontSize: '1rem',
            padding: 0,
            margin: 0,
            borderRadius: theme.spacing(1),
            [`&, .${typographyClasses.root}, .${accordionSummaryClasses.expandIconWrapper}`]:
              {
                transition: theme.transitions.create([
                  'background-color',
                  'color',
                ]),
              },
            [`&, &.${accordionSummaryClasses.expanded}`]: {
              minHeight: '2.5rem',
              padding: '0 0.75rem',
            },
            '&:hover, &:focus, &:focus-visible, &:active': {
              [`&, .${typographyClasses.root}, .${accordionSummaryClasses.expandIconWrapper}`]:
                {
                  color: theme.palette.primary.main,
                },
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }),
          content: {
            margin: 0,
            [`&, &.${accordionSummaryClasses.expanded}`]: {
              margin: 0,
            },
          },
          expandIconWrapper: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: () => ({
            padding: 0,
          }),
        },
      },
      MuiAlert: {
        defaultProps: {
          severity: 'info',
          iconMapping: {
            error: <Error sx={{ fontSize: '1.25rem' }} />,
            info: <Info sx={{ fontSize: '1.25rem' }} />,
            success: <CheckCircle sx={{ fontSize: '1.25rem' }} />,
            warning: <Warning sx={{ fontSize: '1.25rem' }} />,
          },
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(1),
            a: {
              fontWeight: 'bold',
            },
          }),
          icon: ({ theme }) => ({
            display: 'flex',
            padding: 0,
            marginRight: theme.spacing(1),
          }),
          message: {
            padding: 0,
          },
        },
        variants: [
          {
            props: { severity: 'info' },
            style: {
              backgroundColor: alpha(orgTheme.primary, 0.05),
              '&, a': {
                color: alertTextColor(orgTheme.primary, 0.2),
              },
              [`& .${alertClasses.icon}`]: {
                color: orgTheme.primary,
              },
            },
          },
          {
            props: { severity: 'success' },
            style: {
              backgroundColor: alpha(orgTheme.positive, 0.05),
              '&, a': {
                color: alertTextColor(orgTheme.positive, 0.3),
              },
              [`& .${alertClasses.icon}`]: {
                color: orgTheme.positive,
              },
            },
          },
          {
            props: { severity: 'warning' },
            style: {
              backgroundColor: alpha(orgTheme.oriole, 0.05),
              '&, a': {
                color: alertTextColor(orgTheme.oriole, 0.35),
              },
              [`& .${alertClasses.icon}`]: {
                color: orgTheme.oriole,
              },
            },
          },
          {
            props: { severity: 'error' },
            style: {
              backgroundColor: alpha(orgTheme.negative, 0.05),
              '&, a': {
                color: alertTextColor(orgTheme.negative, 0.3),
              },
              [`& .${alertClasses.icon}`]: {
                color: orgTheme.negative,
              },
            },
          },
        ],
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
        styleOverrides: {
          root: {
            [`&.${buttonClasses.disabled}`]: {
              [`&.${buttonClasses.loading}`]: {
                color: 'transparent',
              },
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          variant: 'contained',
          size: 'medium',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            fontSize: '1rem',
            textTransform: 'none',
            fontWeight: 600,
          }),
          sizeSmall: ({ theme }) => ({
            height: 35,
            padding: `0 ${theme.spacing(1)}`,
          }),
          sizeMedium: ({ theme }) => ({
            height: 37,
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
          }),
        },
        variants: [
          /**
           * Contained Variant Overrides
           * */
          {
            props: { variant: 'contained' },
            style: ({ theme }) => ({
              [`&.${buttonClasses.disabled}, &:disabled`]: {
                backgroundColor: alpha(theme.palette.text.disabled, 0.15),
                color: alpha(theme.palette.text.disabled, 0.5),
              },
            }),
          },
          /**
           * Minimal Variant Overrides
           */
          {
            props: { variant: 'minimal' },
            style: ({ theme }) => ({
              [`&.${buttonClasses.disabled}, &:disabled`]: {
                backgroundColor: alpha(theme.palette.text.disabled, 0.15),
                color: alpha(theme.palette.text.disabled, 0.5),
              },
            }),
          },
          {
            props: { variant: 'minimal', color: 'primary' },
            style: ({ theme }) => ({
              backgroundColor: lighten(theme.palette.primary.main, 0.95),
              color: theme.palette.primary.main,
              '&:hover, &:focus, &:focus-visible, &:active': {
                backgroundColor: lighten(theme.palette.primary.main, 0.9),
              },
            }),
          },
          {
            props: { variant: 'minimal', color: 'success' },
            style: ({ theme }) => ({
              backgroundColor: lighten(theme.palette.success.main, 0.95),
              color: theme.palette.success.main,
              '&:hover, &:focus, &:focus-visible, &:active': {
                backgroundColor: lighten(theme.palette.success.main, 0.9),
              },
            }),
          },
          {
            props: { variant: 'minimal', color: 'error' },
            style: ({ theme }) => ({
              backgroundColor: lighten(theme.palette.error.main, 0.95),
              color: theme.palette.error.main,
              '&:hover, &:focus, &:focus-visible, &:active': {
                backgroundColor: lighten(theme.palette.error.main, 0.9),
              },
            }),
          },
          /**
           * Text Variant Overrides
           */
          {
            props: { variant: 'text' },
            style: ({ theme }) => ({
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              [`&.${buttonClasses.disabled}, &:disabled`]: {
                color: alpha(theme.palette.text.disabled, 0.5),
              },
            }),
          },
          {
            props: { variant: 'text', color: 'primary' },
            style: ({ theme }) => ({
              '&:hover, &:focus, &:focus-visible, &:active': {
                color: theme.palette.primary.main,
              },
            }),
          },
          {
            props: { variant: 'text', color: 'success' },
            style: ({ theme }) => ({
              '&:hover, &:focus, &:focus-visible, &:active': {
                color: theme.palette.success.main,
              },
            }),
          },
          {
            props: { variant: 'text', color: 'error' },
            style: ({ theme }) => ({
              '&:hover, &:focus, &:focus-visible, &:active': {
                color: theme.palette.error.main,
              },
            }),
          },
        ],
      },
      MuiIconButton: {
        defaultProps: {
          size: 'medium',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            padding: 0,
            color: theme.palette.text.primary,
            [`&.${buttonClasses.disabled}, &:disabled`]: {
              color: alpha(theme.palette.text.disabled, 0.5),
            },
            [`&.${iconButtonClasses.disabled}`]: {
              [`&.${iconButtonClasses.loading}`]: {
                color: 'transparent',
              },
            },
            '&:hover, &:focus, &:focus-visible, &:active': {
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }),
          sizeSmall: {
            height: 35,
            width: 35,
          },
          sizeMedium: {
            height: 37,
            width: 37,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          variant: 'elevation',
          elevation: 1,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
            outline: '1px solid var(--surface-border)',
            outlineOffset: '-1px',
          }),
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          title: {
            fontWeight: 600,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: 0,

            // required to override default last-child padding
            [`&:last-child`]: {
              paddingBottom: 0,
            },
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: 0,
            justifyContent: 'flex-end',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          input: ({ theme }) => ({
            [`&.${outlinedInputClasses.input}`]: {
              padding: `${theme.spacing(1.25)} ${theme.spacing(
                1.25
              )} ${theme.spacing(1.25)} 0`,
            },
          }),
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            position: 'relative',
            transform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 'normal',
            marginBottom: '0.5rem',
            [`&, &:hover, &.${inputLabelClasses.focused}, &.${inputLabelClasses.error}`]:
              {
                color: theme.palette.text.primary,
              },
          }),
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          notched: false,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            paddingLeft: theme.spacing(1),
            backgroundColor: orgTheme.elementBackground,
            // Hover State
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: orgTheme.elementBorder,
              borderWidth: 1,
            },
            // Focus State
            [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
              {
                borderColor: orgTheme.primary,
                borderWidth: 1,
                boxShadow: `0 0 0 2px ${alpha(orgTheme.primary, 0.25)}`,
              },
            // Error State
            [`&.${outlinedInputClasses.error}`]: {
              [`& .${outlinedInputClasses.notchedOutline}, &:hover .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.palette.error.main,
                  boxShadow: `0 0 0 2px ${alpha(
                    theme.palette.error.main,
                    0.25
                  )}`,
                },
            },
          }),
          notchedOutline: ({ theme }) => ({
            borderColor: orgTheme.elementBorder,
            transition: theme.transitions.create(['border-color', 'boxShadow']),
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(4),
            fontWeight: 'bold',
            height: 'auto',
            lineHeight: 'normal',
          }),
        },
        variants: [
          /**
           * Small Size Overrides
           */
          {
            props: { size: 'small' },
            style: {
              [`& .${chipClasses.label}`]: {
                padding: '3px 0.75rem 4px',
                fontSize: '0.85rem',
              },
            },
          },
          /**
           * Medium Size Overrides
           */
          {
            props: { size: 'medium' },
            style: {
              [`& .${chipClasses.label}`]: {
                padding: '0.4rem 1.25rem',
                fontSize: '1rem',
              },
            },
          },
          /**
           * Large Size Overrides
           */
          {
            props: { size: 'large' },
            style: {
              [`& .${chipClasses.label}`]: {
                padding: '0.5rem 1.25rem',
                fontSize: '1.25rem',
              },
            },
          },
        ],
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            fontSize: 0,
            [`&, &.${formLabelClasses.error}`]: {
              color: orgTheme.contentTertiary,
            },
            '&::after': {
              content: '"(Required)"',
              fontSize: '1rem',
              fontStyle: 'italic',
              marginLeft: '0.25rem',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: '0.25rem 0 0',
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 'normal',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: orgTheme.faintBorder,
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          // The mobile picker dialog otherwise only accepts via its action
          // bar; close on day tap like the desktop popper
          closeOnSelect: true,
          slots: {
            textField: CustomDatePickerTextField,
            actionBar: CustomDatePickerActionBar,
          },
          slotProps: {
            desktopPaper: {
              elevation: 1,
            },
            mobilePaper: {
              elevation: 1,
            },
            // The global MuiIconButton hover/focus styles look off inside the
            // compact picker field: drop the background tint in every state
            // and the lingering focused color, but keep hover color feedback
            clearButton: {
              sx: {
                '&:hover, &:focus, &:focus-visible, &:active': {
                  backgroundColor: 'transparent',
                },
                // Plain :focus lingers after mouse clicks, so only color the
                // keyboard-driven :focus-visible state alongside hover
                '&:focus, &:active': {
                  color: orgTheme.contentPrimary,
                },
                '&:hover, &:focus-visible': {
                  color: orgTheme.primary,
                },
              },
            },
            openPickerButton: {
              sx: {
                '&:hover, &:focus, &:focus-visible, &:active': {
                  backgroundColor: 'transparent',
                },
                // Plain :focus lingers after mouse clicks, so only color the
                // keyboard-driven :focus-visible state alongside hover
                '&:focus, &:active': {
                  color: orgTheme.contentPrimary,
                },
                '&:hover, &:focus-visible': {
                  color: orgTheme.primary,
                },
              },
            },
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, SPACING_FACTOR],
                  },
                },
              ],
            },
            toolbar: {
              hidden: true,
            },
            actionBar: ({ pickerVariant }) => ({
              actions: pickerVariant === 'desktop' ? [] : ['cancel', 'accept'],
            }),
          },
        },
      },
      MuiPickersOutlinedInput: {
        defaultProps: {
          notched: false,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            paddingLeft: theme.spacing(1),
            borderRadius: '0.5rem',
            // Hover State
            [`&:hover .${pickersOutlinedInputClasses.notchedOutline}`]: {
              borderColor: orgTheme.elementBorder,
              borderWidth: 1,
            },
            // Focus State
            [`&.${pickersOutlinedInputClasses.focused} .${pickersOutlinedInputClasses.notchedOutline}`]:
              {
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
                boxShadow: `0 0 0 2px ${alpha(
                  theme.palette.primary.main,
                  0.25
                )}`,
              },
            // Error State
            [`&.${pickersOutlinedInputClasses.error}`]: {
              [`& .${pickersOutlinedInputClasses.notchedOutline}, &:hover .${pickersOutlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.palette.error.main,
                  boxShadow: `0 0 0 2px ${alpha(
                    theme.palette.error.main,
                    0.25
                  )}`,
                },
            },
          }),
          notchedOutline: ({ theme }) => ({
            borderColor: orgTheme.elementBorder,
            transition: theme.transitions.create(['border-color', 'boxShadow']),
          }),
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: orgTheme.contentBackground,
            padding: `0 ${theme.spacing(1)}`,
            height: 'calc(2.5rem + 2px)',
            [`& .${buttonBaseClasses.root}`]: {
              color: orgTheme.contentPrimary,
              marginRight: `-${theme.spacing(0.5)}`,
            },
          }),
          sectionsContainer: {
            padding: 0,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            outline: '1px solid var(--surface-border)',
            outlineOffset: '-1px',
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            outline: '1px solid var(--surface-border)',
            outlineOffset: '-1px',
          }),
        },
      },
      MuiPickerPopper: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.spacing(1),
            outline: '1px solid var(--surface-border)',
            outlineOffset: '-1px',
          }),
        },
      },
      MuiDateCalendar: {
        defaultProps: {
          showDaysOutsideCurrentMonth: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.spacing(1),
          }),
        },
      },
      MuiPickerDay: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '1rem',
            borderRadius: theme.spacing(1),

            '@media (pointer: fine)': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            },

            [`&.${pickerDayClasses.selected}`]: {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,

              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
          }),
          today: ({ theme }) => ({
            fontWeight: 600,
            color: theme.palette.primary.main,
            outlineColor: theme.palette.primary.main,
            borderRadius: theme.spacing(1),

            [`&.${pickerDayClasses.selected}`]: {
              color: theme.palette.primary.contrastText,
            },
          }),
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          placement: 'top',
          disableInteractive: true,
          describeChild: true,
        },
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.grey[800],
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            padding: '5px 9px',
            maxWidth: 350,
            borderRadius: 4,
          }),
          arrow: ({ theme }) => ({
            color: theme.palette.grey[800],
          }),
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          button: ({ theme }) => ({
            fontSize: '1rem',
            borderRadius: theme.spacing(1),

            '&:focus, &:focus-visible': {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.action.focusOpacity
              ),
            },

            '@media (pointer: fine)': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            },

            [`&.${yearCalendarClasses.selected}`]: {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,

              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
    },
  });
};
